import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Shield,
  Search,
  MessageSquare,
  BarChart3,
  AlertTriangle,
  Target,
  Sliders,
  XCircle,
  Lock,
  Mail,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { HeroMockup } from "@/components/landing/HeroMockup";
import { FloorPlanFragment, IsometricStack } from "@/components/landing/Blueprint";
import { CountUp } from "@/components/landing/CountUp";
import { MagneticButton } from "@/components/landing/MagneticButton";

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease },
};

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.55, delay: i * 0.08, ease },
});

const Landing = () => {
  const navigate = useNavigate();
  const goWaitlist = () => navigate("/waitlist");
  const prefersReducedMotion = useReducedMotion();

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 64);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Hero parallax ──
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroCopyY = useTransform(heroProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, -60]);
  const heroCopyOpacity = useTransform(heroProgress, [0, 0.8], [1, 0.4]);
  const heroAuroraY = useTransform(heroProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, 120]);
  const heroMockupY = useTransform(heroProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, -40]);

  return (
    <div className="bone-scope min-h-screen flex flex-col">
      {/* ─── Nav (always visible: solid bone w/ backdrop blur) ─── */}
      <nav
        className={`sticky top-0 z-50 transition-[height,box-shadow] duration-300 bg-bone/85 backdrop-blur-xl border-b ${
          scrolled
            ? "h-14 border-stone-200 shadow-[0_1px_0_0_hsla(36,16%,82%,0.6)]"
            : "h-16 border-stone-200/60"
        }`}
      >
        <div className="max-w-[1200px] mx-auto h-full flex items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-forest flex items-center justify-center">
              <span className="font-serif italic text-bone text-sm font-medium">B</span>
            </div>
            <span className="font-sans text-[15px] font-semibold tracking-tight text-ink">
              Brickshare<span className="text-ochre">.ai</span>
            </span>
          </div>

          <div className="hidden sm:flex items-center gap-7">
            <button
              onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
              className="link-underline font-sans text-[13px] font-medium text-stone-600 hover:text-ink transition-colors"
            >
              How It Works
            </button>
            <button
              onClick={() => document.getElementById("product-preview")?.scrollIntoView({ behavior: "smooth" })}
              className="link-underline font-sans text-[13px] font-medium text-stone-600 hover:text-ink transition-colors"
            >
              Product
            </button>
            <button
              onClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })}
              className="link-underline font-sans text-[13px] font-medium text-stone-600 hover:text-ink transition-colors"
            >
              FAQ
            </button>
            <button
              onClick={() => navigate("/auth")}
              className="link-underline font-sans text-[13px] font-medium text-stone-600 hover:text-ink transition-colors"
            >
              Sign In
            </button>
            <MagneticButton
              onClick={goWaitlist}
              className="btn-forest font-sans text-[13px] font-medium h-9 px-4 rounded-[10px]"
            >
              Request Early Access
            </MagneticButton>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="sm:hidden h-9 w-9 text-ink hover:bg-paper">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-paper border-stone-200">
              <DropdownMenuItem onClick={() => navigate("/auth")}>Sign In</DropdownMenuItem>
              <DropdownMenuItem onClick={goWaitlist}>Join Waitlist</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* ══════════════════════════════════════════ 1) HERO ══════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="relative px-6 pt-16 pb-20 sm:pt-20 sm:pb-28 lg:pt-28 lg:pb-36 overflow-hidden"
      >
        {/* Atmospheric layered background w/ parallax */}
        <motion.div
          style={{ y: heroAuroraY }}
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
        >
          <div className="hero-aurora" />
          <div className="hero-grid-overlay" />
        </motion.div>

        <div className="relative max-w-[1200px] mx-auto grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left — copy with scroll parallax */}
          <motion.div
            style={{ y: heroCopyY, opacity: heroCopyOpacity }}
            className="lg:col-span-7 space-y-7"
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease, delay: 0.05 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-stone-200 bg-paper/80 backdrop-blur-sm shadow-brand-sm"
            >
              <Lock className="w-3.5 h-3.5 text-ochre" />
              <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-stone-600">
                Private Beta — Access by Approval
              </span>
            </motion.div>

            {/* Display headline — pure serif, dramatic scale, italic + color accent for contrast */}
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
              }}
              className="font-serif text-[clamp(3.25rem,8vw,6rem)] leading-[0.98] tracking-[-0.035em] font-normal text-ink"
            >
              <motion.span
                variants={{
                  hidden: { opacity: 0, y: 28 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease } },
                }}
                className="block"
              >
                AI Deal Intelligence
              </motion.span>
              <motion.span
                variants={{
                  hidden: { opacity: 0, y: 28 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease } },
                }}
                className="block"
              >
                for{" "}
                <span className="italic font-medium text-forest">Multifamily</span>
              </motion.span>
              <motion.span
                variants={{
                  hidden: { opacity: 0, y: 28 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease } },
                }}
                className="block"
              >
                Investors.
              </motion.span>
            </motion.h1>

            {/* Sub-copy — sans, lighter weight; clear typographic switch from display serif */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease, delay: 0.5 }}
              className="font-sans text-body-lg text-stone-600 max-w-xl"
            >
              Analyze deals, detect hidden risk, and uncover negotiation insights in minutes.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease, delay: 0.6 }}
              className="font-sans text-body text-stone-600 max-w-lg"
            >
              Built for brokers, investors, and acquisitions teams evaluating multifamily opportunities.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease, delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-3 pt-2"
            >
              <MagneticButton
                onClick={goWaitlist}
                className="btn-forest font-sans text-[15px] font-medium h-12 px-6 rounded-[10px]"
              >
                <span>Request Early Access</span>
                <ArrowRight className="btn-arrow w-4 h-4" />
              </MagneticButton>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease, delay: 0.85 }}
              className="space-y-1 pt-4 border-t border-stone-200/60 max-w-md"
            >
              <p className="font-mono text-[12px] text-stone-600">
                We are onboarding a limited number of early users.
              </p>
            </motion.div>
          </motion.div>

          {/* Right — mockup collage with scroll parallax */}
          <motion.div style={{ y: heroMockupY }} className="lg:col-span-5 lg:-mr-8">
            <HeroMockup />
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ 2) PRODUCT VALUE — bento ══════════════════════════════════════════ */}
      <section className="px-6 py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <h2 className="font-serif-display text-display-lg font-normal text-ink">
              Understand Every Deal in Minutes
            </h2>
            <div className="h-px w-12 bg-ochre mt-6" />
          </motion.div>

          <div className="grid md:grid-cols-3 md:auto-rows-fr gap-5 md:min-h-[420px]">
            {/* Deal Intelligence */}
            <motion.div {...stagger(0)} className="bento-card p-7 flex flex-col relative overflow-hidden">
              <FloorPlanFragment className="absolute -bottom-16 -right-16 w-[260px] text-forest-deep opacity-[0.06]" />
              <div className="w-10 h-10 rounded-full bg-bone border border-stone-200 flex items-center justify-center mb-5 relative">
                <Search className="w-[18px] h-[18px] text-ochre" strokeWidth={1.5} />
                <span className="absolute inset-0 rounded-full bg-ochre/20 animate-ochre-pulse pointer-events-none" />
              </div>
              <h3 className="font-sans text-h3 font-semibold tracking-tight text-ink mb-3 relative">
                Deal Intelligence
              </h3>
              <p className="font-ui text-body text-stone-600 leading-relaxed relative">
                Instantly analyze acquisitions using structured underwriting logic and a transparent deal score.
              </p>
            </motion.div>

            {/* Risk Detection — hero card with embedded mini-readout */}
            <motion.div
              {...stagger(1)}
              className="bento-card p-7 md:col-span-2 relative overflow-hidden flex flex-col md:flex-row md:items-stretch gap-7"
            >
              <IsometricStack className="absolute -bottom-16 -right-16 w-[320px] text-forest-deep opacity-[0.06]" />
              <div className="flex-1 flex flex-col relative">
                <div className="w-10 h-10 rounded-full bg-bone border border-stone-200 flex items-center justify-center mb-5">
                  <Shield className="w-[18px] h-[18px] text-ochre" strokeWidth={1.5} />
                  <span className="absolute w-10 h-10 rounded-full bg-ochre/20 animate-ochre-pulse pointer-events-none" />
                </div>
                <h3 className="font-sans text-h3 font-semibold tracking-tight text-ink mb-3">
                  Risk Detection
                </h3>
                <p className="font-ui text-body-lg text-stone-600 leading-relaxed">
                  Identify aggressive assumptions, fragile returns, and execution risks before committing to a deal.
                </p>
              </div>
              <div className="md:w-[200px] flex md:flex-col md:justify-center gap-3 relative">
                {[
                  { label: "Vacancy", v: 62 },
                  { label: "Capex", v: 78 },
                  { label: "Leverage", v: 45 },
                ].map((r, i) => (
                  <div key={r.label} className="flex-1 md:flex-none flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <span className="eyebrow text-stone-400">
                        {r.label}
                      </span>
                      <span className="font-mono text-2xs tabular-nums text-stone-600">
                        {r.v}
                      </span>
                    </div>
                    <div className="h-1 w-full rounded-full bg-stone-200 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${r.v}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 + i * 0.1, ease }}
                        className="h-full rounded-full bg-ochre"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Negotiation Insights — full-width row */}
            <motion.div
              {...stagger(2)}
              className="bento-card p-7 md:col-span-3 flex flex-col md:flex-row md:items-center gap-6"
            >
              <div className="w-10 h-10 rounded-full bg-bone border border-stone-200 flex items-center justify-center shrink-0 relative">
                <MessageSquare className="w-[18px] h-[18px] text-ochre" strokeWidth={1.5} />
                <span className="absolute inset-0 rounded-full bg-ochre/20 animate-ochre-pulse pointer-events-none" />
              </div>
              <div className="flex-1">
                <h3 className="font-sans text-h3 font-semibold tracking-tight text-ink mb-2">
                  Negotiation Insights
                </h3>
                <p className="font-ui text-body-lg text-stone-600 leading-relaxed">
                  Discover what price, rent assumptions, or capex levels would make a deal viable.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ 3) HOW IT WORKS ══════════════════════════════════════════ */}
      <section id="how-it-works" className="px-6 py-24 lg:py-32 bg-paper">
        <div className="max-w-[1200px] mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <h2 className="font-serif-display text-display-lg font-normal text-ink">
              From Opportunity to Decision in Minutes
            </h2>
            <div className="h-px w-12 bg-ochre mt-6" />
          </motion.div>

          <div className="relative grid sm:grid-cols-3 gap-6">
            {/* Connecting blueprint line */}
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, delay: 0.3, ease }}
              style={{ transformOrigin: "left center" }}
              className="hidden sm:block absolute top-[34px] left-[8%] right-[8%] h-px bg-stone-200 z-0"
            />
            {[
              { step: "01", icon: Sliders, title: "Enter Deal Assumptions", desc: "Input basic acquisition, rent, capex, and financing assumptions." },
              { step: "02", icon: BarChart3, title: "Analyze the Opportunity", desc: "Brickshare evaluates returns, risk factors, and sensitivity across scenarios." },
              { step: "03", icon: Target, title: "Decide or Negotiate", desc: "Understand whether to pursue the deal or renegotiate the terms." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                {...stagger(i)}
                className="relative bg-bone border border-stone-200 rounded-brand-lg p-6 z-10"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-full bg-paper border border-stone-200 flex items-center justify-center">
                    <item.icon className="w-[18px] h-[18px] text-ochre" strokeWidth={1.5} />
                  </div>
                  <span className="font-serif italic text-[34px] text-ochre/80 leading-none">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-sans text-h4 font-semibold tracking-tight text-ink mb-2">{item.title}</h3>
                <p className="font-ui text-[14px] text-stone-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ 4) PRODUCT PREVIEW — dark band ══════════════════════════════════════════ */}
      <section id="product-preview" className="relative px-6 py-24 lg:py-32 bg-ink overflow-hidden">
        <IsometricStack className="absolute -top-20 -right-20 w-[700px] text-bone opacity-[0.05]" />
        <div className="max-w-[1200px] mx-auto relative">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <h2 className="font-serif-display text-display-lg font-normal text-bone">
              Built for Real Deal Workflows
            </h2>
            <div className="h-px w-12 bg-ochre mt-6" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease }}
            className="bg-paper rounded-brand-xl border border-stone-200 p-6 sm:p-10 shadow-brand-lg"
          >
            <motion.div
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="font-mono text-[11px] text-stone-400 uppercase tracking-[0.08em]">
                Sample Deal Report
              </span>
              <span className="h-px flex-1 bg-stone-200" />
              <span className="font-mono text-[11px] text-stone-400 uppercase tracking-[0.08em]">
                Brickshare Output
              </span>
            </motion.div>

            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="space-y-2"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-stone-400">
                  Decision
                </span>
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-forest text-bone font-mono text-[11px] font-semibold uppercase tracking-[0.1em]">
                  EARNS DILIGENCE
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className="space-y-2"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-stone-400">
                  Deal Score
                </span>
                <div className="flex items-center gap-3">
                  <div className="h-1.5 flex-1 rounded-full bg-stone-200 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "82%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.2, delay: 0.7, ease }}
                      className="h-full rounded-full bg-forest"
                    />
                  </div>
                  <CountUp end={82} duration={1.2} className="font-mono text-h3 font-medium text-ink tabular-nums leading-none" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.5 }}
                className="space-y-2"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-stone-400">
                  Risk Level
                </span>
                <div className="font-sans text-h4 font-semibold tracking-tight text-ink">Moderate</div>
              </motion.div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {[
                { icon: CheckCircle2, color: "text-forest", label: "DSCR", text: "1.32x — Above 1.20 threshold" },
                { icon: AlertTriangle, color: "text-ochre", label: "Vacancy", text: "12% — Verify against submarket" },
                { icon: CheckCircle2, color: "text-forest", label: "LTV", text: "72% — Within safe range" },
                { icon: XCircle, color: "text-clay", label: "Hard Stop", text: "None detected" },
              ].map((flag, i) => (
                <motion.div
                  key={flag.label}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -12 : 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.5 + i * 0.08 }}
                  className="flex items-start gap-3 p-3.5 rounded-brand-md border border-stone-200 bg-bone"
                >
                  <flag.icon className={`w-4 h-4 ${flag.color} mt-0.5 shrink-0`} strokeWidth={1.5} />
                  <div>
                    <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-stone-400">
                      {flag.label}
                    </span>
                    <p className="font-ui text-[13px] text-ink font-medium">{flag.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="space-y-3">
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="font-mono text-[11px] uppercase tracking-[0.08em] text-stone-400 block"
              >
                Pillar Scores
              </motion.span>
              {[
                { name: "Income", score: 78 },
                { name: "Expense", score: 85 },
                { name: "Leverage", score: 72 },
                { name: "Market", score: 80 },
                { name: "Execution", score: 68 },
              ].map((pillar, i) => (
                <motion.div
                  key={pillar.name}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: 0.65 + i * 0.07 }}
                  className="flex items-center gap-3"
                >
                  <span className="font-mono text-[11px] w-20 text-stone-400 uppercase tracking-[0.08em]">
                    {pillar.name}
                  </span>
                  <div className="h-1.5 flex-1 rounded-full bg-stone-200 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${pillar.score}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.9, delay: 0.8 + i * 0.07, ease }}
                      className={`h-full rounded-full ${
                        pillar.score >= 75 ? "bg-forest" : pillar.score >= 60 ? "bg-ochre" : "bg-clay"
                      }`}
                    />
                  </div>
                  <span className="font-mono text-[12px] w-8 text-right text-ink tabular-nums">
                    {pillar.score}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <p className="text-center font-serif-display italic text-stone-400 mt-8 text-[15px]">
            Designed to replace messy spreadsheets with structured deal intelligence.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════ 5) TRUST ══════════════════════════════════════════ */}
      <section className="px-6 py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="font-serif-display text-display-lg font-normal text-ink">
              Designed for Professional Deal Analysis
            </h2>
            <p className="font-ui text-body-lg text-stone-600 max-w-xl mx-auto leading-relaxed mt-6">
              Brickshare brings institutional underwriting logic into a simple workflow designed for brokers, investors, and acquisitions teams.
            </p>
            <div className="h-px w-12 bg-ochre mx-auto mt-6" />
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {[
              "Structured underwriting model",
              "Transparent scoring logic",
              "Sensitivity and risk detection",
              "Negotiation scenario testing",
            ].map((item, i) => (
              <motion.div
                key={item}
                {...stagger(i)}
                className="bento-card flex items-center gap-3 p-5"
              >
                <CheckCircle2 className="w-4 h-4 text-ochre shrink-0" strokeWidth={1.5} />
                <span className="font-ui text-[14px] font-medium text-ink">{item}</span>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <MagneticButton
              onClick={goWaitlist}
              className="btn-outline font-ui text-[14px] font-medium h-11 px-6 rounded-[10px]"
            >
              <span>See How to Get Started</span>
              <ArrowRight className="btn-arrow w-4 h-4 text-ochre" />
            </MagneticButton>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ 6) FAQ ══════════════════════════════════════════ */}
      <section id="faq" className="px-6 py-24 lg:py-32 bg-paper">
        <div className="max-w-[820px] mx-auto">
          <motion.div {...fadeUp} className="mb-14 text-center">
            <h2 className="font-serif-display text-display-lg font-normal text-ink">
              Frequently Asked Questions
            </h2>
          </motion.div>
          <div className="border-t border-stone-200">
            {[
              {
                q: "Is this financial advice?",
                a: "No. Brickshare is a pre-diligence screening tool. It does not calculate returns, provide appraisals, or make investment recommendations. Results depend on the accuracy of your inputs.",
              },
              {
                q: "What data do I need to start?",
                a: "Basic deal assumptions: purchase price, unit count, rent, vacancy, and financing terms. No T-12 or rent roll required for the initial screen.",
              },
              {
                q: "How does the scoring work?",
                a: "The scoring is deterministic, not predictive. It checks your inputs against structured benchmarks across income, expense, leverage, market, and execution risk pillars.",
              },
              {
                q: "What happens to my data?",
                a: "Your deal data is stored securely and only accessible to your account. We don't share, sell, or use your data for anything except showing you your results.",
              },
              {
                q: "Does this replace full underwriting?",
                a: "No. This is the gate before underwriting. It tells you whether a deal deserves the time, money, and attention of full diligence.",
              },
            ].map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ 7) PRIVATE BETA CTA ══════════════════════════════════════════ */}
      <section className="relative px-6 py-24 lg:py-32 bg-forest-gradient overflow-hidden">
        <FloorPlanFragment className="absolute inset-0 w-full h-full text-bone opacity-[0.06]" />
        <div className="max-w-[760px] mx-auto text-center space-y-8 relative">
          <motion.div {...fadeUp} className="space-y-7">
            <h2 className="font-serif-display text-display-lg font-normal text-bone">
              Private Beta Access
            </h2>
            <p className="font-ui text-body-lg text-bone/70 max-w-md mx-auto leading-relaxed">
              We are currently onboarding a limited number of early users while refining the platform.
            </p>
            <div className="flex justify-center">
              <MagneticButton
                onClick={goWaitlist}
                className="btn-bone font-ui text-[15px] font-medium h-12 px-7 rounded-[10px]"
              >
                <span>Request Early Access</span>
                <ArrowRight className="btn-arrow w-4 h-4" />
              </MagneticButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ 8) FOOTER ══════════════════════════════════════════ */}
      <footer className="bg-ink px-6 py-16">
        <div className="max-w-[1200px] mx-auto space-y-10">
          <div className="grid sm:grid-cols-2 gap-10 items-start">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-ochre flex items-center justify-center">
                  <span className="font-serif italic text-ink text-sm font-medium">B</span>
                </div>
                <span className="font-sans text-h4 font-semibold tracking-tight text-bone">
                  Brickshare<span className="text-ochre">.ai</span>
                </span>
              </div>
              <p className="font-mono text-[12px] text-stone-400 max-w-xs">
                AI Deal Intelligence for Multifamily Investors
              </p>
            </div>

            <div className="flex flex-wrap items-start sm:justify-end gap-x-8 gap-y-3">
              <button
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                className="link-underline font-mono text-[12px] text-stone-400 hover:text-bone transition-colors"
              >
                How It Works
              </button>
              <button
                onClick={goWaitlist}
                className="link-underline font-mono text-[12px] text-stone-400 hover:text-bone transition-colors"
              >
                Waitlist
              </button>
              <a href="/privacy" className="link-underline font-mono text-[12px] text-stone-400 hover:text-bone transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="link-underline font-mono text-[12px] text-stone-400 hover:text-bone transition-colors">
                Terms
              </a>
              <a
                href="mailto:hello@brickshare.ai"
                className="link-underline font-mono text-[12px] text-stone-400 hover:text-bone transition-colors flex items-center gap-1"
              >
                <Mail className="w-3 h-3" /> Contact
              </a>
            </div>
          </div>

          <div className="h-px w-full bg-ochre/30" />

          <p className="font-mono text-[11px] text-stone-400/80 text-center">
            Not investment advice. Results depend on user-provided inputs. © 2026 Brickshare.
          </p>
        </div>
      </footer>
    </div>
  );
};

/* ─── FAQ Accordion Item ─── */
const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [open, setOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="border-b border-stone-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left group"
        aria-expanded={open}
      >
        <span className="font-ui text-body-lg font-medium text-ink pr-6 group-hover:text-forest transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-stone-400 shrink-0 transition-transform duration-300 ${
            open ? "rotate-180 text-ochre" : ""
          }`}
          strokeWidth={1.5}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={
              prefersReducedMotion
                ? { duration: 0.001 }
                : { height: { duration: 0.35, ease }, opacity: { duration: 0.25 } }
            }
            style={{ overflow: "hidden" }}
          >
            <p className="font-ui text-body text-stone-600 leading-relaxed pb-6 pr-10 max-w-2xl">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Landing;
